import * as T from "../../../utilities/frontendTypes";
import textData from "../../../textData.json";

export type FormCompletenessState = "complete" | "incomplete";
export type FounderProfileSectionKey = "personalInfo" | "companyInfo" | "pitchDeck" | "needHelpWith" | "iPStrategy" | "ipReadiness" | "programingEvaluation" | "review";

interface ProfileSideNavProps {
  currentHTML: FounderProfileSectionKey;
  setCurrentHTML: (section: FounderProfileSectionKey) => void;
  memberApplicationStatus: T.MemberApplicationStatus;
  personalInfoComplete: FormCompletenessState;
  companyInfoComplete: FormCompletenessState;
  pitchDeckComplete: FormCompletenessState;
  needHelpWithComplete: FormCompletenessState;
  programingEvaluationComplete: FormCompletenessState;
  langState: T.LangState;
}

const IncompleteCircle = () => (
  <svg className="shrink-0 border border-text rounded-full p-[2px] m-1.5" width="24" height="24" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#F7F7F7"/>
  </svg>
);

const CurrentCircle = () => (
  <svg className="shrink-0 border border-text rounded-full p-[2px] m-1.5" width="24" height="24" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="8" fill="#F8CE7D"/>
  </svg>
);

const CompleteCircle = () => (
  <svg className="shrink-0 m-1.5" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <g clipPath="url(#clip0_2385_761)">
      <path d="M3 13.0148C3 14.4613 3.28491 15.8937 3.83846 17.23C4.392 18.5664 5.20335 19.7807 6.22617 20.8035C7.249 21.8263 8.46326 22.6377 9.79965 23.1912C11.136 23.7448 12.5684 24.0297 14.0148 24.0297C15.4613 24.0297 16.8937 23.7448 18.23 23.1912C19.5664 22.6377 20.7807 21.8263 21.8035 20.8035C22.8263 19.7807 23.6377 18.5664 24.1912 17.23C24.7448 15.8937 25.0297 14.4613 25.0297 13.0148C25.0297 11.5684 24.7448 10.136 24.1912 8.79965C23.6377 7.46326 22.8263 6.249 21.8035 5.22617C20.7807 4.20335 19.5664 3.392 18.23 2.83846C16.8937 2.28491 15.4613 2 14.0148 2C12.5684 2 11.136 2.28491 9.79965 2.83846C8.46326 3.392 7.249 4.20335 6.22617 5.22617C5.20335 6.249 4.392 7.46326 3.83846 8.79965C3.28491 10.136 3 11.5684 3 13.0148Z" fill="#EBF6F0"/>
      <path d="M11.9992 23.0141C9.0779 23.0141 6.27623 21.8536 4.21055 19.7879C2.14486 17.7222 0.984375 14.9205 0.984375 11.9992C0.984375 9.0779 2.14486 6.27623 4.21055 4.21055C6.27623 2.14486 9.0779 0.984375 11.9992 0.984375V23.0141Z" fill="#F7F7F7"/>
      <path d="M0.984375 11.9992C0.984375 13.4457 1.26928 14.878 1.82283 16.2144C2.37638 17.5508 3.18772 18.7651 4.21055 19.7879C5.23337 20.8107 6.44764 21.6221 7.78402 22.1756C9.1204 22.7292 10.5527 23.0141 11.9992 23.0141C13.4457 23.0141 14.878 22.7292 16.2144 22.1756C17.5508 21.6221 18.7651 20.8107 19.7879 19.7879C20.8107 18.7651 21.6221 17.5508 22.1756 16.2144C22.7292 14.878 23.0141 13.4457 23.0141 11.9992C23.0141 10.5527 22.7292 9.1204 22.1756 7.78402C21.6221 6.44764 20.8107 5.23337 19.7879 4.21055C18.7651 3.18773 17.5508 2.37638 16.2144 1.82283C14.878 1.26928 13.4457 0.984375 11.9992 0.984375C10.5527 0.984375 9.1204 1.26928 7.78402 1.82283C6.44764 2.37638 5.23337 3.18773 4.21055 4.21055C3.18772 5.23337 2.37638 6.44764 1.82283 7.78402C1.26928 9.1204 0.984375 10.5527 0.984375 11.9992Z" stroke="#1A2E25" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17.7477 7.21094L11.043 16.3102L6.25391 12.4789" stroke="#1A2E25" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs><clipPath id="clip0_2385_761"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

function ProfileSideNav({
  currentHTML,
  setCurrentHTML,
  memberApplicationStatus,
  personalInfoComplete,
  companyInfoComplete,
  pitchDeckComplete,
  needHelpWithComplete,
  programingEvaluationComplete,
  langState,
}: ProfileSideNavProps) {
  // Sections are editable forms — lock navigation for every status where the
  // application must not change, not just "pending" (an approved/mid-funnel
  // founder could otherwise still edit their profile through these links).
  // rejectedReapply stays editable: those founders update their profile to reapply.
  const isLocked = !["unsubmitted", "pendingEditRequest", "rejectedReapply"].includes(memberApplicationStatus ?? "");
  const t = textData.Pages.FounderPages.FounderProfile;

  const getCircle = (section: FounderProfileSectionKey, complete: FormCompletenessState) => {
    if (currentHTML === section) return <CurrentCircle />;
    if (complete === "complete") return <CompleteCircle />;
    return <IncompleteCircle />;
  };

  const getButtonClass = (section: FounderProfileSectionKey) => {
    if (isLocked) return "cursor-not-allowed text-text/50";
    if (currentHTML === section) return "font-semibold";
    return "hover:underline";
  };

  const sections: { key: FounderProfileSectionKey; label: string; complete: FormCompletenessState; disabled?: boolean }[] = [
    { key: "personalInfo", label: t.YourInfo[langState], complete: personalInfoComplete },
    { key: "companyInfo", label: t.CompanyInfo[langState], complete: companyInfoComplete },
    { key: "pitchDeck", label: t.SidenavPitchDeck[langState], complete: pitchDeckComplete },
    { key: "needHelpWith", label: t.SidenavNeedHelp[langState], complete: needHelpWithComplete },
    { key: "programingEvaluation", label: t.ServiceEval[langState], complete: programingEvaluationComplete },
  ];

  return (
    <div className="applicationSideMenu">
      <div className="flex flex-col gap-8 max-w-[226px]" id="applicationSideMenuInner">
        <div className="flex flex-col">
          <h3 className="mb-8 font-bold">{t.YourApp[langState]}</h3>
          {sections.map((section, index) => (
            <div key={section.key} className="flex flex-col">
              <div className="flex items-center flex-row-reverse justify-end w-full">
                <button
                  onClick={() => setCurrentHTML(section.key)}
                  disabled={isLocked}
                  className={`text-left ${getButtonClass(section.key)}`}
                >
                  {section.label}
                </button>
                {getCircle(section.key, section.complete)}
              </div>
              {index < sections.length - 1 && (
                <div className="h-8 w-full relative">
                  <div className="h-full w-px bg-secondary ml-[17px]" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProfileSideNav;
